import { ArrowDown, Badge, CheckCheck, ChevronDown, Copy, CopyCheck, Forward, Frown, Languages, LockKeyhole, LockKeyholeOpen, Mail, MailWarning, Meh, Pen, Pencil, Phone, Smile, SmilePlus, SquareUser, X } from 'lucide-react';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { EditDetail } from './EditDetail';
import { CreateContact } from './CreateContact';
import { EditableVital } from './CaseSidebar';
// Removed Dropdown import since we're using native select elements
import { getContactInitialsClassName } from '../utils/colorUtils';
import { formatValueByType } from '../utils/formatUtils';
import Modal from './Modal';
import Alert from './Alert';
import { getCookie, setCookie } from '../utils/cookies';

const MetaItem = ({ icon, value, main = null, unique = null, type = null, onClick, copyValue = null }) => {
    const [copyTimer, setCopyTimer] = useState(null);

    useEffect(() => {
        if (copyTimer) {
            const timer = setTimeout(() => {
                setCopyTimer(null);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [copyTimer]);

    if (!value) return null;
    return (
        <div className='item' onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
            {icon}
            {type === 'phone' ? <a href={`tel:${value}`}>{value}</a> 
                : type === 'email' ? <a href={`mailto:${value}${unique && unique !== value ? `?cc=${unique}` : ''}`}>{value}</a> 
                : value}
            {copyValue && !copyTimer ? (
                <Copy 
                    className='copy-icon'
                    size={16}
                    onClick={() => {
                        navigator.clipboard.writeText(copyValue || value);
                        setCopyTimer(true);
                    }}
                />
            ) : copyTimer ? (
                <CheckCheck
                    className='copy-icon'
                    size={16}
                />
            ) : null}
        </div>
    );
};

export const CaseHeader = forwardRef(({ caseData = {}, fetchCase, vitals = [], fetchVitals, caseTypes, fetchFields, openVitals, setOpenVitals, onVitalUpdate, onSettlementFieldsRequiredChange, onDisengagementFieldsRequiredChange, onFetchPhases }, ref) => {
    const contact = caseData?.contact || {};
    const caseInfo = caseData?.case || {};
    const caseIdDisplay = caseInfo?.id ?? "N/A";
    const [tags, setTags] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [tagInput, setTagInput] = useState("");
    const tagRef = useRef(null);
    const [editContact, setEditContact] = useState(false);
    const [phases, setPhases] = useState([]);
    const [subphases, setSubphases] = useState([]);
    const [currentPhase, setCurrentPhase] = useState(null);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [editingCaseName, setEditingCaseName] = useState(false);
    const [caseNameInput, setCaseNameInput] = useState("");
    const [createContact, setCreateContact] = useState(false);
    const [users, setUsers] = useState([]);
    const [showNatureModal, setShowNatureModal] = useState(false);
    const [selectedNature, setSelectedNature] = useState(null);
    const [natureDescription, setNatureDescription] = useState("");
    const [message, setMessage] = useState({ message: '', type: '' });
    const [showLockModal, setShowLockModal] = useState(false);
    const [showVitalsInHeader, setShowVitalsInHeader] = useState(() => {
        const saved = getCookie('show_vitals_in_header');
        if (saved === null) return true;
        return saved === '1';
    });
    const phase19Options = caseData?.phase19_options || [];
    const phase19Value = caseInfo?.phase19_value || '';
    const phase19SelectValue = phase19Value === null || phase19Value === undefined ? '' : String(phase19Value);
    const mortgagePhaseOptions = caseData?.mortgage_phase_options || [];
    const mortgagePhaseValue = caseInfo?.mortgage_phase_value || '';
    const mortgagePhaseSelectValue = mortgagePhaseValue === null || mortgagePhaseValue === undefined ? '' : String(mortgagePhaseValue);
    const showMortgagePhase = caseInfo?.phase_id === 19 && phase19SelectValue === '7';
    const feesOwedSubphase = subphases.find(subphase => String(subphase.phase || '').trim().toLowerCase() === 'fees owed') || null;
    const medicalUpdateSubphases = feesOwedSubphase ? subphases.filter(subphase => subphase.id !== feesOwedSubphase.id) : subphases;
    const caseNameRef = useRef(null);
    const [pendingSettlementPhase, setPendingSettlementPhase] = useState(null);
    const [settlementFieldsRequired, setSettlementFieldsRequired] = useState(false);
    const [pendingDisengagementPhase, setPendingDisengagementPhase] = useState(null);
    const pendingSettlementPhaseRef = useRef(null);
    const caseInfoRef = useRef(null);
    const caseHeaderRef = useRef(null);
    const mobileSummaryRef = useRef(null);
    const mobileSummaryNameRef = useRef(null);
    const mobileSummaryPhaseRef = useRef(null);
    const caseNameDisplayRef = useRef(null);
    const casePhaseRef = useRef(null);
    const [copyTimer, setCopyTimer] = useState(null);
    const [mobileCompactHeader, setMobileCompactHeader] = useState(false);
    const [mobileHeaderOpacity, setMobileHeaderOpacity] = useState(1);
    const [mobileSummaryMotion, setMobileSummaryMotion] = useState({ nameOffset: 0, nameOpacity: 0, phaseOffset: 0, phaseOpacity: 0 });

    useEffect(() => {
        if (copyTimer) {
            const timer = setTimeout(() => {
                setCopyTimer(null);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [copyTimer]);

    // Keep refs in sync with state/props
    useEffect(() => {
        pendingSettlementPhaseRef.current = pendingSettlementPhase;
        caseInfoRef.current = caseInfo;
    }, [pendingSettlementPhase, caseInfo]);

    // Expose fetchPhases through ref for Section to call
    useImperativeHandle(ref, () => ({
        fetchPhases: fetchPhases
    }), []);

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (screenWidth >= 768) {
            setMobileCompactHeader(false);
            setMobileHeaderOpacity(1);
            return;
        }

        let ticking = false;

        const getScrollTop = () => {
            const caseBody = document.querySelector('.case-body');
            const caseSection = document.querySelector('.case-section');
            return Math.max(
                window.scrollY || 0,
                document.documentElement?.scrollTop || 0,
                caseBody?.scrollTop || 0,
                caseSection?.scrollTop || 0
            );
        };

        const updateCompactState = () => {
            ticking = false;
            const scrollTop = getScrollTop();
            const fullHeaderHeight = caseHeaderRef.current?.offsetHeight || 0;
            const compactHeaderHeight = mobileSummaryRef.current?.offsetHeight || 0;
            const fadeDistance = Math.max(fullHeaderHeight - compactHeaderHeight, 1);
            const nextOpacity = Math.max(0, Math.min(1, 1 - (scrollTop / fadeDistance)));
            const scrollProgress = 1 - nextOpacity;
            const summaryTop = mobileSummaryRef.current?.getBoundingClientRect().top || 0;
            const getMotionOffset = (sourceElement, targetElement) => {
                if (!sourceElement || !targetElement || scrollTop <= 0) {
                    return 0;
                }

                const sourceTop = sourceElement.getBoundingClientRect().top;
                const targetTop = summaryTop + targetElement.offsetTop;
                return Math.max(0, sourceTop - targetTop);
            };
            const phaseOffset = getMotionOffset(casePhaseRef.current, mobileSummaryPhaseRef.current);
            const phaseEntryDistance = 48;
            const nameStartOffset = mobileSummaryRef.current && mobileSummaryNameRef.current
                ? Math.max(0, compactHeaderHeight - mobileSummaryNameRef.current.offsetTop)
                : 0;
            const nameProgress = Math.min(1, scrollProgress * 1.8);
            const nextMotion = {
                nameOffset: (1 - nameProgress) * nameStartOffset,
                nameOpacity: Math.min(1, scrollTop / 16),
                phaseOffset: phaseOffset,
                phaseOpacity: Math.max(0, Math.min(1, 1 - (phaseOffset / phaseEntryDistance)))
            };
            setMobileCompactHeader(scrollTop > 0);
            setMobileHeaderOpacity((currentValue) => Math.abs(currentValue - nextOpacity) < 0.01 ? currentValue : nextOpacity);
            setMobileSummaryMotion((currentValue) => {
                if (
                    Math.abs(currentValue.nameOffset - nextMotion.nameOffset) < 0.5 &&
                    Math.abs(currentValue.nameOpacity - nextMotion.nameOpacity) < 0.02 &&
                    Math.abs(currentValue.phaseOffset - nextMotion.phaseOffset) < 0.5 &&
                    Math.abs(currentValue.phaseOpacity - nextMotion.phaseOpacity) < 0.02
                ) {
                    return currentValue;
                }
                return nextMotion;
            });
        };

        const handleScroll = () => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(updateCompactState);
        };

        let caseBody = null;
        const attachCaseBodyScroll = () => {
            caseBody = document.querySelector('.case-body');
            caseBody?.addEventListener('scroll', handleScroll, { passive: true });
            updateCompactState();
        };

        const animationFrame = window.requestAnimationFrame(attachCaseBodyScroll);
        const attachTimeout = window.setTimeout(attachCaseBodyScroll, 250);
        updateCompactState();
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            window.cancelAnimationFrame(animationFrame);
            window.clearTimeout(attachTimeout);
            window.removeEventListener('scroll', handleScroll, true);
            caseBody?.removeEventListener('scroll', handleScroll);
        };
    }, [screenWidth, caseInfo?.id]);

    useEffect(() => {
        if (!caseHeaderRef.current) {
            return;
        }

        const root = document.documentElement;
        const updateHeaderHeight = () => {
            const height = caseHeaderRef.current?.offsetHeight || 0;
            root.style.setProperty('--case-header-height', `${height}px`);
        };

        updateHeaderHeight();

        const observer = new ResizeObserver(() => {
            updateHeaderHeight();
        });
        observer.observe(caseHeaderRef.current);

        return () => {
            observer.disconnect();
        };
    }, [caseInfo, screenWidth, showVitalsInHeader]);

    useEffect(() => {
        const root = document.documentElement;

        if (screenWidth >= 768) {
            root.style.removeProperty('--case-mobile-summary-height');
            return;
        }

        const updateSummaryHeight = () => {
            const height = mobileSummaryRef.current?.offsetHeight || 0;
            root.style.setProperty('--case-mobile-summary-height', `${height}px`);
        };

        updateSummaryHeight();

        const observer = new ResizeObserver(updateSummaryHeight);
        if (mobileSummaryRef.current) {
            observer.observe(mobileSummaryRef.current);
        }
        window.addEventListener('resize', updateSummaryHeight);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateSummaryHeight);
        };
    }, [screenWidth, caseInfo?.case_name, caseInfo?.phase_id]);

    useEffect(() => {
        setCookie('show_vitals_in_header', showVitalsInHeader ? '1' : '0', 365);
    }, [showVitalsInHeader]);

    useEffect(() => {
        try {
            const parsedTags = caseInfo?.tags ? Object.values(JSON.parse(caseInfo.tags)) : [];
            setTags(parsedTags);
        } catch (error) {
            console.error("Error parsing tags:", error);
            setTags([]);
        }
    }, [caseInfo?.tags]);

    useEffect(() => {
        if (editingIndex !== null) {
            tagRef.current?.focus();
        }

        const handleClickOutside = (e) => {
            if (tagRef.current && !tagRef.current.contains(e.target)) {
                setEditingIndex(null);
            }
            if (caseNameRef.current && !caseNameRef.current.contains(e.target) && editingCaseName) {
                handleSaveCaseName();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [editingIndex, editingCaseName]);

    useEffect(() => {
        if (editingCaseName) {
            caseNameRef.current?.focus();
        }
    }, [editingCaseName]);

    const fetchPhases = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/phases.php`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setPhases(data.phases || []);
                setSubphases(data.subphases || []);
                // Notify parent (Case.js) that phases were fetched, so it can sync Section
                if (onFetchPhases) {
                    onFetchPhases(data.phases || [], data.subphases || []);
                }
            } else {
                console.error("Failed to fetch phases:", data.message);
            }
        } catch (error) {
            console.error("Error fetching phases:", error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/user.php`);
            const data = await response.json();
            if (data.success) {
                setUsers(data.users || []);
            } else {
                console.error("Failed to fetch users:", data.message);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };



    const updatePhase = async (phaseId, isSubphase = false, skipSettlementCheck = false) => {
        try {
            // Reset settlement flow at the start of a new phase update attempt
            if (!skipSettlementCheck) {
                setPendingSettlementPhase(null);
                setSettlementFieldsRequired(false);
                setPendingDisengagementPhase(null);
                if (onSettlementFieldsRequiredChange) {
                    onSettlementFieldsRequiredChange(false);
                }
                if (onDisengagementFieldsRequiredChange) {
                    onDisengagementFieldsRequiredChange(false);
                }
                window.dispatchEvent(new CustomEvent('clearSettlementState'));
                window.dispatchEvent(new CustomEvent('clearDisengagementState'));
            }

            // Determine the payload based on whether it's a subphase or main phase
            const payload = {
                case_id: caseInfo.id,
                phase_id: phaseId,
                is_subphase: isSubphase
            };

            const response = await fetch(`https://api.casedb.co/phases.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
                credentials: "include"
            });

            const data = await response.json();
            console.log('Phase update response:', data);
            console.log('fields_missing value:', data.fields_missing, 'type:', typeof data.fields_missing);
            
            if (!data.success) {
                console.error("Failed to update phase:", data.message);
                
                if (data.requires_disengagement_fields !== undefined) {
                    console.log('Disengagement phase detected, fields_missing:', data.fields_missing);
                    setPendingDisengagementPhase(phaseId);

                    if (onDisengagementFieldsRequiredChange) {
                        onDisengagementFieldsRequiredChange(!!data.fields_missing);
                    }

                    if (data.fields_missing) {
                        window.dispatchEvent(new CustomEvent('navigateToDisengagement', {
                            detail: {
                                pendingPhase: phaseId,
                                missingFieldIds: data.missing_disengagement_field_ids || []
                            }
                        }));
                    } else {
                        window.dispatchEvent(new CustomEvent('navigateToDisengagementActivity', {
                            detail: {
                                pendingPhase: phaseId,
                                draft: data.disengagement_activity_draft || null
                            }
                        }));
                    }
                    console.log('Disengagement workflow handoff complete');
                } else if (data.fields_missing !== undefined) {
                    console.log('Settlement phase detected, fields_missing:', data.fields_missing);
                    
                    // This is a settlement phase verification flow
                    // This may be a restart - update the pending phase for the new attempt
                    setPendingSettlementPhase(phaseId);
                    setSettlementFieldsRequired(true);
                    
                    // Notify parent component to highlight settlement fields
                    if (onSettlementFieldsRequiredChange) {
                        onSettlementFieldsRequiredChange(true);
                    }
                    
                    // Route based on whether fields are missing or just need verification
                    if (data.fields_missing) {
                        // Fields are missing - send to section 20 to fill them
                        console.log('Settlement fields missing, navigating to settlement section');
                        window.dispatchEvent(new CustomEvent('navigateToSettlement', { detail: { pendingPhase: phaseId } }));
                    } else {
                        // Fields are filled but need verification - send directly to section 24
                        console.log('Settlement fields ready for verification, dispatching navigateToVerification event with phaseId:', phaseId);
                        const event = new CustomEvent('navigateToVerification', { detail: { sectionId: 24, pendingPhase: phaseId } });
                        console.log('Event object:', event);
                        window.dispatchEvent(event);
                        console.log('Event dispatched');
                        window.dispatchEvent(new CustomEvent('settlementVerificationNotice', {
                            detail: {
                                message: data.message || 'Settlement fields verified. Please confirm to proceed.'
                            }
                        }));
                    }
                    console.log('Settlement verification flow complete');
                } else {
                    console.log('No fields_missing field in response, showing error message');
                    setMessage({ message: data.message || 'Failed to update phase', type: 'error' });
                }
            } else {
                // Clear settlement state if it was pending
                if (pendingSettlementPhase) {
                    setPendingSettlementPhase(null);
                    setSettlementFieldsRequired(false);
                    
                    // Notify parent component to remove settlement field highlighting
                    if (onSettlementFieldsRequiredChange) {
                        onSettlementFieldsRequiredChange(false);
                    }
                }
                if (pendingDisengagementPhase) {
                    setPendingDisengagementPhase(null);
                    if (onDisengagementFieldsRequiredChange) {
                        onDisengagementFieldsRequiredChange(false);
                    }
                }
                // Refresh case data after successful update
                fetchCase(true);
            }
        } catch (error) {
            console.error("Error updating phase:", error);
            setMessage({ message: 'Error updating phase', type: 'error' });
        }
    };

    // Note: Automatic phase update on settlementFieldsSaved has been removed.
    // Phase change now requires user confirmation in section 24 via VerifyData component.

    // Listen for settlement state clear event after successful phase update
    useEffect(() => {
        const handleClearSettlementState = () => {
            setPendingSettlementPhase(null);
            setSettlementFieldsRequired(false);
            
            // Notify parent component
            if (onSettlementFieldsRequiredChange) {
                onSettlementFieldsRequiredChange(false);
            }
        };

        window.addEventListener('clearSettlementState', handleClearSettlementState);
        return () => window.removeEventListener('clearSettlementState', handleClearSettlementState);
    }, [onSettlementFieldsRequiredChange]);

    useEffect(() => {
        const handleClearDisengagementState = () => {
            setPendingDisengagementPhase(null);
            if (onDisengagementFieldsRequiredChange) {
                onDisengagementFieldsRequiredChange(false);
            }
        };

        window.addEventListener('clearDisengagementState', handleClearDisengagementState);
        return () => window.removeEventListener('clearDisengagementState', handleClearDisengagementState);
    }, [onDisengagementFieldsRequiredChange]);

    const updatePhase19Field = async (value) => {
        try {
            const response = await fetch(`https://api.casedb.co/cases.php?phase19_field=1`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ case_id: caseInfo.id, value }),
                credentials: "include"
            });

            const data = await response.json();
            if (!data.success) {
                console.error("Failed to update phase 19 field:", data.message);
            } else if (fetchCase) {
                fetchCase(true);
            }
        } catch (error) {
            console.error("Error updating phase 19 field:", error);
        }
    };

    const updateMortgagePhaseField = async (value) => {
        try {
            const response = await fetch(`https://api.casedb.co/cases.php?mortgage_phase_field=1`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ case_id: caseInfo.id, value }),
                credentials: "include"
            });

            const data = await response.json();
            if (!data.success) {
                console.error("Failed to update mortgage phase field:", data.message);
            } else if (fetchCase) {
                fetchCase(true);
            }
        } catch (error) {
            console.error("Error updating mortgage phase field:", error);
        }
    };

    const handlePhase19Change = (e) => {
        // Persist the option index (0-based) as requested
        const selectedIndex = e.target.selectedIndex - 1; // offset for placeholder
        if (selectedIndex < 0) {
            updatePhase19Field('');
            return;
        }
        updatePhase19Field(String(selectedIndex));
    };

    const handleMortgagePhaseChange = (e) => {
        // Persist the option index (0-based) to match field storage
        const selectedIndex = e.target.selectedIndex - 1; // offset for placeholder
        if (selectedIndex < 0) {
            updateMortgagePhaseField('');
            return;
        }
        updateMortgagePhaseField(String(selectedIndex));
    };

    const handleEditTag = (index) => {
        setEditingIndex(index);
        setTagInput(tags[index]);
    };

    const handleSaveTag = async (index) => {
        if (!tagInput.trim()) return;
    
        const updatedTags = [...tags];
        updatedTags[index] = tagInput.trim();
        setTags(updatedTags);
        setEditingIndex(null);
        await updateTags(updatedTags);
    };    

    const handleAddTag = async () => {
        const newTag = "NewTag";
        const newTags = [...tags, newTag];
    
        setTags(newTags);
        setEditingIndex(tags.length);
        setTagInput(newTag);
        await updateTags(newTags);
    };    

    const handleDeleteTag = async (index) => {
        const newTags = tags.filter((_, i) => i !== index);
        setTags(newTags);
        await updateTags(newTags);
    };

    const updateTags = async (updatedTags) => {
        try {
            const response = await fetch(`https://api.casedb.co/update-tags.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ case_id: caseInfo.id, tags: updatedTags }),
            });

            const data = await response.json();
            if (!data.success) {
                console.error("Failed to update tags:", data.message);
            }
        } catch (error) {
            console.error("Error updating tags:", error);
        }
    };

    const handleEditCaseName = () => {
        setEditingCaseName(true);
        setCaseNameInput(caseInfo?.case_name || "");
    };

    const handleSaveCaseName = async () => {
        if (!caseNameInput.trim()) {
            setEditingCaseName(false);
            return;
        }

        try {
            const response = await fetch(`https://api.casedb.co/cases.php`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ 
                    id: caseInfo.id, 
                    case_name: caseNameInput.trim() 
                }),
                credentials: 'include'
            });

            const data = await response.json();
            if (data.success) {
                fetchCase(true); // Refresh case data
                setEditingCaseName(false);
            } else {
                console.error("Failed to update case name:", data.message);
            }
        } catch (error) {
            console.error("Error updating case name:", error);
        }
    };

    const handleCancelCaseNameEdit = () => {
        setCaseNameInput(caseInfo?.case_name || "");
        setEditingCaseName(false);
    };

    const handleNewContactCreated = (newContact) => {
        // Refresh the case data to show the new contact
        if (fetchCase && typeof fetchCase === 'function') {
            fetchCase(true);
        }
        setCreateContact(false);
        
        // If there's a callback stored for this contact creation, call it
        if (window.contactCreationCallback && typeof window.contactCreationCallback === 'function') {
            window.contactCreationCallback(newContact);
            window.contactCreationCallback = null; // Clear the callback
        }
    };

    const handleOpenNatureModal = () => {
        setSelectedNature(contact?.nature || null);
        setNatureDescription(contact?.nature_description || "");
        setShowNatureModal(true);
    };

    const handleSaveNature = async () => {
        try {
            const response = await fetch('https://api.casedb.co/contacts.php', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    contact_id: contact.id,
                    nature: selectedNature,
                    nature_description: natureDescription
                })
            });

            const data = await response.json();
            if (data.success) {
                fetchCase(true);
                setShowNatureModal(false);
            } else {
                console.error('Failed to update nature:', data.message);
            }
        } catch (error) {
            console.error('Error updating nature:', error);
        }
    };

    const updateVital = async (field, newValue, table, customVitalName = null) => {
        const firstPrimaryId = (() => {
            const fp = vitals?.find(v => v.name === "First Primary" || v.field_id === -2);
            return fp?.original_value || null;
        })();

        const payload = {
            case_id: caseInfo?.id ?? caseInfo?.case_id,
            lead_id: caseData?.lead?.id,
            field,
            value: newValue,
            table
        };

        if (customVitalName) {
            payload.custom_vital_name = customVitalName;
            payload.is_custom_vital = true;
            if (firstPrimaryId) payload.user_id = firstPrimaryId;
        }

        try {
            const response = await fetch("https://api.casedb.co/update-vital.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result.success) {
                fetchVitals();
                if (table === 'leads' || table === 'cases')
                    fetchCase(true);
                // If case_type_id is updated, refresh fields
                if (field === 'case_type_id') {
                    fetchFields?.();
                }
                
                // Notify parent component that a vital was updated
                if (onVitalUpdate && typeof onVitalUpdate === 'function') {
                    onVitalUpdate({ field, newValue, table });
                }
                setMessage({ message: 'Vital updated successfully.', type: 'success' });
            } else {
                console.error("Update failed:", result.message);
                setMessage({ message: 'Failed to update vital.', type: 'error' });
            }
        } catch (error) {
            console.error("Error updating vital:", error);
            setMessage({ message: 'An error occurred while updating vital.', type: 'error' });
        }
    };

    const contactDisplay = contact?.profile_picture
        ? <img src={`https://api.casedb.co/${contact.profile_picture}`} alt="Profile" />
        : <h2>{contact?.is_company ? contact?.company_name?.trim()?.charAt(0) : `${contact?.first_name ? contact.first_name?.trim().charAt(0) : 'D'}${contact?.last_name ? contact?.last_name?.trim().charAt(0) : 'B'}` ?? "DB"}</h2>;

    useEffect(() => {
        fetchPhases();
        fetchUsers();
    }, []);

    useEffect(() => {
        // Set current phase state to match the case data
        const phaseToSet = caseInfo?.subphase_id || caseInfo?.phase_id;
        if (phaseToSet) {
            setCurrentPhase(phaseToSet);
        }
    }, [caseData]);

    return (
        <>
        {screenWidth < 768 && (
            <div ref={mobileSummaryRef} className={`case-header-mobile-summary ${mobileCompactHeader ? 'visible' : ''}`} aria-hidden={!mobileCompactHeader}>
                <div ref={mobileSummaryNameRef} className='case-header-mobile-summary-name' style={{ opacity: mobileSummaryMotion.nameOpacity, transform: `translateY(${mobileSummaryMotion.nameOffset}px)` }}>
                    {caseInfo?.case_name || 'Loading...'}
                </div>
                <div ref={mobileSummaryPhaseRef} className='case-header-mobile-summary-phase' style={{ opacity: mobileSummaryMotion.phaseOpacity, pointerEvents: mobileSummaryMotion.phaseOpacity > 0.95 ? 'auto' : 'none', transform: `translateY(${mobileSummaryMotion.phaseOffset}px)` }}>
                    <div className='select-wrapper'>
                        <select
                            title='Phase'
                            className='filter-select'
                            value={caseInfo?.phase_id || ''}
                            onChange={(e) => updatePhase(e.target.value, false)}
                        >
                            <option value=''>Select Phase</option>
                            {phases.map(phase => (
                                <option key={phase.id} value={phase.id}>
                                    {phase.phase}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={16} className='select-chevron' />
                    </div>
                </div>
            </div>
        )}
        <div className='case-header' ref={caseHeaderRef} style={screenWidth < 768 ? { '--case-mobile-header-opacity': mobileHeaderOpacity } : undefined}>
            <div className='case-information-container'>
                <div className='case-information'>
                    {screenWidth >= 768 && 
                        <div className={getContactInitialsClassName('case-initials', caseInfo?.case_id)}>
                            {contactDisplay}
                        </div>
                    }
                    <div className='details'>
                        <h2 className='case-header-title'>
                            {screenWidth < 768 && 
                                <div className={getContactInitialsClassName('case-initials-small', caseInfo?.case_id)}>
                                    {contactDisplay}
                                </div>
                            }
                            {editingCaseName ? (
                                <input
                                    ref={caseNameRef}
                                    type="text"
                                    value={caseNameInput}
                                    onChange={(e) => setCaseNameInput(e.target.value)}
                                    onBlur={handleSaveCaseName}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSaveCaseName();
                                        } else if (e.key === 'Escape') {
                                            handleCancelCaseNameEdit();
                                        }
                                    }}
                                    className="case-name-input"
                                />
                            ) : (
                                <span 
                                    ref={caseNameDisplayRef}
                                    className="case-name-editable"
                                    onClick={handleEditCaseName}
                                >
                                    {caseInfo?.case_name || "Loading..."}
                                </span>
                            )}
                            <span className='subtext case-header-id' style={{ display: 'flex', alignItems: 'center', gap: '2.5px' }}>
                                ({caseIdDisplay})
                                {!copyTimer ? (
                                    <Copy 
                                        className='copy-icon'
                                        size={16}
                                        onClick={() => {
                                            navigator.clipboard.writeText(String(caseIdDisplay));
                                            setCopyTimer(true);
                                        }}
                                    />
                                ) : (
                                    <CheckCheck
                                        className='copy-icon'
                                        size={16}
                                    />
                                )}
                            </span>
                            <span className='confidential-badge' title='Confidential Case' style={{ color: 'var(--subtext-color)' }} onClick={() => setShowLockModal(true)}>
                                {caseInfo?.confidential ? <LockKeyhole size={16}/> : <LockKeyholeOpen size={16} />}
                            </span>
                            <div className='contact-nature' onClick={handleOpenNatureModal} style={{ cursor: 'pointer' }}>
                                {contact?.nature === 1 ? <Smile size={18} className="contact-nature-icon well-natured" /> : contact?.nature === 2 ? <Meh size={18} className="contact-nature-icon neutral-natured" /> : contact?.nature === 3 ? <Frown size={18} className="contact-nature-icon ill-natured" /> : <SmilePlus size={18} className="contact-nature-icon subtext" />}
                            </div>
                            {contact?.language && contact.language.toLowerCase() !== 'english' && (
                                <Languages 
                                    size={18} 
                                    className="language-indicator" 
                                    title={`Needs an interpreter for ${contact.language}`}
                                />
                            )}
                        </h2>
                        {screenWidth >= 768 ? (
                            <div className='case-header-meta subtext'>
                                {contact && <MetaItem icon={<SquareUser size={16} />} value={contact?.is_company ? contact?.company_name ?? contact?.full_name : contact?.full_name} onClick={() => setEditContact(true)} />}
                                {contact?.details?.phone && <MetaItem icon={<Phone size={16} />} value={contact?.details?.phone ? contact?.details?.phone[0]?.number : ""} type='phone' copyValue={contact?.details?.phone ? contact?.details?.phone[0]?.number : ""} />}
                                {contact?.details?.email && <MetaItem icon={<Mail size={16} />} value={contact?.details?.email ? contact?.details?.email[0]?.email : ""} unique={caseInfo?.unique_email} type='email' copyValue={contact?.details?.email ? contact?.details?.email[0]?.email : ""} />}
                                {caseInfo?.unique_email && <MetaItem icon={<Forward size={16} />} value={caseInfo?.unique_email ? caseInfo?.unique_email : ""} main={contact?.details?.email ? contact?.details?.email[0]?.email : ""} unique={caseInfo?.unique_email} type='email' copyValue={caseInfo?.unique_email ? caseInfo?.unique_email : ""} />}
                            </div>
                        ) : (
                            <div className='filter-group'>
                                <div className='select-wrapper'>
                                    <select className='filter-select' style={{ backgroundColor: 'var(--background-color)' }} title='Contact Info'>
                                        {contact && <option value={contact?.full_name} onClick={() => setEditContact(true)}>
                                            {contact?.full_name || "No Contact"}
                                        </option>}
                                        {contact?.details?.phone?.[0]?.number && <option value={contact?.details?.phone?.[0]?.number || ""} type='phone'>
                                            {contact?.details?.phone?.[0]?.number || "No Phone"}
                                        </option>}
                                        {contact?.details?.email?.[0]?.email && <option value={contact?.details?.email?.[0]?.email || ""} type='email'>
                                            {contact?.details?.email?.[0]?.email || "No Email"}
                                        </option>}
                                        {caseInfo?.unique_email && <option value={caseInfo?.unique_email || ""} main={contact?.details?.email?.[0]?.email || ""} unique={caseInfo?.unique_email} type='email'>
                                            {caseInfo?.unique_email || "No Unique Email"}
                                        </option>}
                                    </select>
                                    <ChevronDown size={16} className="select-chevron" />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className='case-phase' ref={casePhaseRef}>
                    <div className='filter-group case-main-phase-control'>
                        {/*<label className='subtext' htmlFor="main-phase-select">Phase</label>*/}
                        <div className='select-wrapper'>
                            <select 
                                id="main-phase-select" 
                                style={{ backgroundColor: 'var(--background-color)' }}
                                title='Phase'
                                className='filter-select' 
                                value={caseInfo?.phase_id || ""} 
                                onChange={(e) => updatePhase(e.target.value, false)}
                            >
                                <option value="">Select Phase</option>
                                {phases.map(phase => (
                                    <option key={phase.id} value={phase.id}>
                                        {phase.phase}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown size={16} className="select-chevron" />
                        </div>
                    </div>
                    {caseInfo?.phase_id === 14 && <div className='filter-group case-subphase-control'>
                        {/*<label className='subtext' htmlFor="sub-phase-select">Subphase</label>*/}
                        <div className='select-wrapper'>
                            <select 
                                id="sub-phase-select" 
                                title='Subphase'
                                className='filter-select'
                                style={{ backgroundColor: 'var(--background-color)' }} 
                                value={caseInfo?.subphase_id || ""} 
                                onChange={(e) => updatePhase(e.target.value, true)}
                            >
                                <option value="">No active subphase</option>
                                {medicalUpdateSubphases.map(subphase => (
                                    <option key={subphase.id} value={subphase.id}>
                                        {subphase.phase}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown size={16} className="select-chevron" />
                        </div>
                    </div>}
                    {caseInfo?.phase_id === 22 && <div className='filter-group case-subphase-control'>
                        <div className='select-wrapper'>
                            <select 
                                id="referred-out-sub-phase-select" 
                                title='Subphase'
                                className='filter-select'
                                style={{ backgroundColor: 'var(--background-color)' }} 
                                value={caseInfo?.subphase_id || ""} 
                                onChange={(e) => updatePhase(e.target.value, true)}
                            >
                                <option value="">Unknown</option>
                                {feesOwedSubphase && (
                                    <option key={feesOwedSubphase.id} value={feesOwedSubphase.id}>
                                        {feesOwedSubphase.phase}
                                    </option>
                                )}
                            </select>
                            <ChevronDown size={16} className="select-chevron" />
                        </div>
                    </div>}
                    {caseInfo?.phase_id === 19 && (
                        <div className='filter-group case-phase-extra-control'>
                            <div className='select-wrapper'>
                                <select
                                    id="phase19-select"
                                    title='Phase 19 Option'
                                    className='filter-select'
                                    style={{ backgroundColor: 'var(--background-color)' }}
                                    value={phase19SelectValue}
                                    onChange={handlePhase19Change}
                                >
                                    <option value="">Select option</option>
                                    {phase19Options.map((opt, idx) => (
                                        <option key={idx} value={String(idx)}>
                                            {opt?.label ?? opt?.value ?? opt}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                    )}
                    {showMortgagePhase && (
                        <div className='filter-group case-phase-extra-control'>
                            <div className='select-wrapper'>
                                <select
                                    id="mortgage-phase-select"
                                    title='Mortgage Phase'
                                    className='filter-select'
                                    style={{ backgroundColor: 'var(--background-color)' }}
                                    value={mortgagePhaseSelectValue}
                                    onChange={handleMortgagePhaseChange}
                                >
                                    <option value="">Select option</option>
                                    {mortgagePhaseOptions.map((opt, idx) => (
                                        <option key={idx} value={String(idx)}>
                                            {opt?.label ?? opt?.value ?? opt}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className='case-header-data'>
                <div className='case-tags'>
                    {tags.map((tag, index) => (
                        editingIndex === index ? (
                            <input
                                key={`tag-${index}`}
                                ref={tagRef}
                                value={tagInput}
                                className='tag input caps'
                                onChange={(e) => setTagInput(e.target.value)}
                                onBlur={() => handleSaveTag(index)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveTag(index)}
                            />
                        ) : (
                            <span key={`tag-${index}`} className='tag caps' onDoubleClick={() => handleEditTag(index)}>
                                {tag.includes("#") ? tag : `#${tag}`} <span className="remove-tag" onClick={() => handleDeleteTag(index)} style={{ color: "var(--text-color)", display: 'flex', justifyContent: 'center', alignItems: 'center' }}><X size="12" /></span>
                            </span>
                        )
                    ))}
                    <span className='tag caps bright edit' onClick={handleAddTag}>
                        <Pencil size={16} /> Add Tag
                    </span>
                </div>
            </div>
            {(screenWidth >= 768 && showVitalsInHeader) && (
                <div className='case-vitals'>
                    {/* Only render hardcoded Case Type if it's not already in the vitals array */}
                    {!vitals.some(vital => vital.name === "Case Type" || vital.field_id === 10) && (
                        <EditableVital
                            label="Case Type"
                            value={(() => {
                                // Convert case type ID to array index for display
                                if (!Array.isArray(caseTypes) || caseTypes.length === 0) {
                                    console.log("Case Types not loaded or empty");
                                    return "";
                                }
                                // Filter out case types with id = 0
                                const filteredCaseTypes = caseTypes.filter(ct => ct.id && String(ct.id) !== "0");
                                const currentCaseTypeId = String(caseData?.lead?.case_type_id || "");
                                
                                console.log("Case Type Debug:", {
                                    allCaseTypes: caseTypes,
                                    filteredCaseTypes: filteredCaseTypes,
                                    currentCaseTypeId: currentCaseTypeId,
                                    rawCaseTypeId: caseData?.lead?.case_type_id
                                });
                                
                                if (currentCaseTypeId && currentCaseTypeId !== "") {
                                    // Find the index in the filtered array
                                    const currentIndex = filteredCaseTypes.findIndex(ct => String(ct.id) === currentCaseTypeId);
                                    console.log("Found index:", currentIndex, "for case type ID:", currentCaseTypeId);
                                    // Return the index as string since select values are strings
                                    return currentIndex >= 0 ? String(currentIndex) : "";
                                }
                                return "";
                            })()}
                            typeId={10}
                            field="case_type_id"
                            table="leads"
                            updateVital={(field, newValue, table) => {
                                // Convert array index back to case type ID
                                if (Array.isArray(caseTypes) && (newValue === 0 || (newValue !== null && newValue !== "" && newValue !== undefined))) {
                                    // Filter out case types with id = 0 (use same logic as value calculation)
                                    const filteredCaseTypes = caseTypes.filter(ct => ct.id && String(ct.id) !== "0");
                                    
                                    const selectedIndex = parseInt(newValue);
                                    
                                    if (!isNaN(selectedIndex) && selectedIndex >= 0 && filteredCaseTypes[selectedIndex]) {
                                        const selectedCaseType = filteredCaseTypes[selectedIndex];
                                        updateVital(field, selectedCaseType.id, table);
                                    } else {
                                        updateVital(field, newValue, table);
                                    }
                                } else {
                                    updateVital(field, newValue, table);
                                }
                            }}
                            options={Array.isArray(caseTypes) ? caseTypes.filter(ct => ct.id && String(ct.id) !== "0").map(ct => ct.name) : []}
                            onCreateNewContact={(callback) => {
                                if (callback && typeof callback === 'function') {
                                    window.contactCreationCallback = callback;
                                }
                                setCreateContact(true);
                            }}
                            inCaseHeader={true}
                        />
                    )}
                    {/* Hardcoded First Primary vital display - always show */}
                    <EditableVital
                        label="First Primary"
                        value={(() => {
                            // Find First Primary in vitals array
                            const firstPrimaryVital = vitals.find(vital => vital.name === "First Primary");
                            return firstPrimaryVital ? firstPrimaryVital.value : null;
                        })()}
                        originalValue={vitals?.find(vital => vital.name === "First Primary")?.original_value}
                        typeId={8}
                        field="first_primary_id"
                        table="cases"
                        updateVital={updateVital}
                        options={[]}
                        users={users}
                        inCaseHeader={true}
                    />
                    {/* Filter and deduplicate vitals array before mapping */}
                    {(() => {
                        // Safely handle vitals array
                        if (!Array.isArray(vitals) || vitals.length === 0) {
                            return null;
                        }

                        // Filter vitals safely
                        const filteredVitals = vitals?.filter(vital => {
                            // Skip if vital is null/undefined or missing required properties
                            if (!vital || typeof vital !== 'object') {
                                return false;
                            }

                            // Skip First Primary vital since it's hardcoded above
                            if (vital.name === "First Primary" || vital.field_id === -2) {
                                return false;
                            }

                            // Always show custom fields without a saved vital row
                            if (vital.vital_id == null) {
                                return true;
                            }

                            // Show vitals with null case_type_id (universal vitals)
                            if (vital.case_type_id === null || vital.case_type_id === undefined) {
                                return true;
                            }

                            // Treat case_type_id 0 as global
                            const vitalCaseType = parseInt(vital.case_type_id);
                            if (vitalCaseType === 0) {
                                return true;
                            }

                            // Show vitals that match the current case type
                            const currentCaseType = parseInt(caseData?.lead?.case_type_id);
                            
                            // Only match if both are valid numbers
                            if (isNaN(vitalCaseType) || isNaN(currentCaseType)) {
                                return false;
                            }
                            
                            return vitalCaseType === currentCaseType;
                        });

                        // Deduplicate using a simpler approach
                        const seenIds = new Set();
                        const uniqueVitals = filteredVitals?.filter(vital => {
                            const id = vital?.id || vital?.field_id || vital?.name;
                            if (seenIds.has(id)) {
                                return false;
                            }
                            seenIds.add(id);
                            return true;
                        });

                        // Map to components
                        return uniqueVitals?.map((vital, index) => {
                            // Parse options for the component
                            let options = [];

                            if (vital?.options) {
                                try {
                                    options = JSON.parse(vital?.options);
                                } catch (e) {
                                    options = [];
                                }
                            }

                            return (
                                <EditableVital
                                    key={vital?.id || `vital-${index}`}
                                    label={vital?.name}
                                    value={vital?.value}
                                    typeId={vital?.type_id}
                                    field={vital?.field_id}
                                    table="custom_fields"
                                    customVitalName={!vital?.field_id ? vital?.name : null}
                                    updateVital={updateVital}
                                    formatDate={(date) => {}}
                                    options={options}
                                    contactName={vital?.contact_name}
                                    onCreateNewContact={(callback) => {
                                        if (callback && typeof callback === 'function') {
                                            window.contactCreationCallback = callback;
                                        }
                                        setCreateContact(true);
                                    }}
                                    inCaseHeader={true}
                                />
                            );
                        });
                    })()}
                </div>
            )}

            {screenWidth >= 768 && 
                <div className='minimize-vitals' onClick={() => setShowVitalsInHeader(prev => !prev)} title='Minimize Vitals'>
                    <ChevronDown size='18' style={{ transform: showVitalsInHeader ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                </div>
            }

            {editContact && (
                <EditDetail 
                    setEditContact={setEditContact} 
                    contactData={contact} 
                    fetchContacts={fetchCase} 
                    setCreateContact={setCreateContact}
                    caseContact={true}
                />
            )}
            {createContact && (
                <CreateContact 
                    setCreateContact={setCreateContact} 
                    onContactCreated={handleNewContactCreated} 
                />
            )}
            {showLockModal && (
                <Modal
                    onClose={() => setShowLockModal(false)}
                    footer={
                        <div className='modal-footer-actions center-footer'>
                            <button
                                onClick={() => setShowLockModal(false)}
                                className="action alt"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={async () => {
                                    try {
                                        const response = await fetch(`https://api.casedb.co/cases.php`, {
                                            method: "PUT",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({ 
                                                id: caseInfo.id, 
                                                confidential: caseInfo?.confidential ? 0 : 1
                                            }),
                                            credentials: 'include'
                                        });

                                        const data = await response.json();
                                        if (data.success) {
                                            fetchCase(true); // Refresh case data
                                            setShowLockModal(false);
                                        } else {
                                            console.error("Failed to update confidentiality:", data.message);
                                        }
                                    } catch (error) {
                                        console.error("Error updating confidentiality:", error);
                                    }
                                }}
                                className="action"
                            >
                                Confirm
                            </button>
                        </div>
                    }
                >
                    <div className="modal-content-wrapper">
                        {caseInfo?.confidential ? (
                            <>
                                <h2 style={{ width: '100%', textAlign: 'center', marginBottom: '0' }}>Mark Case as Open</h2>
                                <p style={{ textAlign: 'center', fontSize: '14px' }}>Are you sure you want to mark this case as open? This will make the case accessible to all standard users.</p>
                            </>
                        ) : (
                            <>
                                <h2 style={{ width: '100%', textAlign: 'center', marginBottom: '0' }}>Mark Case as Confidential</h2>
                                <p style={{ textAlign: 'center', fontSize: '14px' }}>Are you sure you want to mark this case as confidential? This will restrict access to the case based on team assignments.</p>
                            </>
                        )}
                    </div>
                </Modal>
            )}
            {showNatureModal && (
                <Modal
                    title="Contact Nature"
                    onClose={() => setShowNatureModal(false)}
                    footer={
                        <div className='modal-footer-actions'>
                            <button
                                onClick={() => setShowNatureModal(false)}
                                className="action alt"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveNature}
                                className="action"
                                disabled={selectedNature === null}
                            >
                                Save
                            </button>
                        </div>
                    }
                >
                    <div className="modal-content-wrapper">
                        <div className='smiles-container'>
                            <div
                                onClick={() => setSelectedNature(1)}
                                className={`smile-container ${selectedNature === 1 ? 'selected' : ''}`}
                            >
                                <Smile size={30} className="contact-nature-icon well-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Well Natured</div>
                            </div>
                            <div
                                onClick={() => setSelectedNature(2)}
                                className={`smile-container ${selectedNature === 2 ? 'selected' : ''}`}
                            >
                                <Meh size={30} className="contact-nature-icon neutral-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Neutral</div>
                            </div>
                            <div
                                onClick={() => setSelectedNature(3)}
                                className={`smile-container ${selectedNature === 3 ? 'selected' : ''}`}
                            >
                                <Frown size={30} className="contact-nature-icon ill-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Ill Natured</div>
                            </div>
                        </div>

                        <div className='form-group full' style={{ maxWidth: '100%', width: '100%' }}>
                            <label className="subtext">
                                Nature Description
                            </label>
                            <textarea
                                value={natureDescription}
                                onChange={(e) => setNatureDescription(e.target.value)}
                                placeholder="Add notes about this contact's nature..."
                            />
                        </div>
                    </div>
                </Modal>
            )}
            {message.message.length > 0 && <Alert message={message.message} type={message.type} onClose={() => setMessage({ message: '', type: '' })} /> }
        </div>
        </>
    );
});
